<script setup>

import { ref, inject, computed, watch, nextTick } from 'vue';
import {
	getLibelleExpression } from '@/scripts/global.js';
import { 
	apiModifierExpression,
	apiModifierNomVariableCalculable,
	apiModifierTypeFormatExpressions } from '@/services/ServiceModele.js';
import ListeAutoCompletion from '@/components/generiques/ListeAutoCompletion.vue';
import SelecteurTypeFormat from '@/components/generiques/SelecteurTypeFormat.vue';
import FormuleAffichee from '@/components/generiques/FormuleAffichee.vue';

const emit = defineEmits(['annuler']);

const modeEdition = ref(false);
const texteExpression = ref('');
const texteNomVariable = ref('');
const selectionTypeFormat = ref(null);
const composantSaisie = ref(null);
const autoCompletionEstActivee = ref(false);
const evenementAutoCompletion = ref({});

const calculables = inject('calculablesSelectionnes');
const getTypeDonnee = inject('getTypeDonnee');
const getTypeObjet = inject('getTypeObjet');
const getTypeFormat = inject('getTypeFormat');
const getTypesFormat = inject('getTypesFormat');
const evenement = inject('evenement');
const appelerApiModifModele = inject('appelerApiModifModele');

const calculable = computed(() => {
	if (calculables.value.length === 1) {
		return calculables.value[0];
	} else {
		return null;
	}
});

const typeDonnee = computed(() => {
	var typeDonnee = null;
	for (const calc of calculables.value) {
		if (typeDonnee) {
			if (typeDonnee !== getTypeDonnee(calc.expression.typeDonnee))
				return null;
		} else {
			typeDonnee = getTypeDonnee(calc.expression.typeDonnee);
		}
	}
	return typeDonnee;
});

const typesFormatProposes = computed(() => {
    if (typeDonnee.value) {
        return getTypesFormat(typeDonnee.value);
    } else {
        return null;
    }
});

watch(() => calculables.value, (calculablesSel) => nextTick(() => {
	modeEdition.value = false;
	if (calculablesSel.length === 0) {
		texteNomVariable.value = '';
		texteExpression.value = '';
		selectionTypeFormat.value = null;
	} else if (calculablesSel.length === 1) {
		let calc = calculablesSel[0];
		texteNomVariable.value = calc?.nomVariable;
		texteExpression.value = calc?.expression.texte;
		selectionTypeFormat.value = getTypeFormat(calc.expression.idTypeFormat);
	} else if (calculablesSel.length > 1) {
		texteNomVariable.value = '';
		texteExpression.value = '';
		let idTypesFormat = Array.from(new Set(calculablesSel.map(calc => calc.expression.idTypeFormat)));
		selectionTypeFormat.value = (idTypesFormat.length === 1 ? getTypeFormat(idTypesFormat[0]) : null);
	}
}));

watch(() => calculable.value?.expression?.idTypeFormat, (idTypeFormat) => {
	selectionTypeFormat.value = getTypeFormat(idTypeFormat);
});

watch(() => evenement.value.editionExpression, (edition) => {
    if (edition) {
		activerModeEdition();
		delete evenement.value.editionExpression;
	}
});

function activerModeEdition() {
	modeEdition.value = true;
	nextTick(() => {
		composantSaisie.value.focus();	
	});
}

function activerAutoCompletion(activee) {
	autoCompletionEstActivee.value = activee;
}

function selectionnerSuggestionSuivante(increment) {
	evenementAutoCompletion.value.increment = increment;
}

function validerAutoCompletion(infosCompletion) {
	if (!infosCompletion.motComplete) {
		autoCompletionEstActivee.value = false;
		modifierExpression();
		return;
	}
	activerAutoCompletion(false);
	const positionDebMotComplete = infosCompletion.expressionCourante.positionDebutMotCourant;
	const positionTexteApres = positionDebMotComplete + infosCompletion.expressionCourante.dernierMot.length;
	const motComplete = infosCompletion.motComplete;
	const texteAvant = texteExpression.value.substring(0, positionDebMotComplete);
	const texteApres = texteExpression.value.substring(positionTexteApres);
	texteExpression.value = `${texteAvant}${motComplete}${texteApres}`;
	nextTick(() => {
		composantSaisie.value.selectionStart = positionDebMotComplete + motComplete.length;
		composantSaisie.value.selectionEnd = composantSaisie.value.selectionStart;
		composantSaisie.value.focus();
	});
}

function annuler() {
	if (calculables.value.length === 1) {
		texteExpression.value = calculables.value[0].expression.texte;
	}
	emit('annuler');
	autoCompletionEstActivee.value = false;
	modeEdition.value = false;
}

function gererPerteFocus(event) {
	if (event.relatedTarget?.className !== 'elt-auto-completion') {
		annuler();
	}
}

function gererToucheClavier(event) {
	switch (event.key) {
		case 'Enter':
			event.preventDefault();
			if (autoCompletionEstActivee.value) {
				evenementAutoCompletion.value.validation = true;
			} else if (calculable.value) {
				modifierExpression();
			}			
			break;
		case 'Escape':
			event.preventDefault();
			annuler();
			break;
		case 'ArrowDown':
			event.preventDefault();
			selectionnerSuggestionSuivante(1);
			break;
		case 'ArrowUp':
			event.preventDefault();
			selectionnerSuggestionSuivante(-1);
			break;
		case 'PageDown':
			event.preventDefault();
			selectionnerSuggestionSuivante(10);
			break;
		case 'PageUp':
			event.preventDefault();
			selectionnerSuggestionSuivante(-10);
			break;
	}
}

function modifierNomVariable() {
	console.log("Modif nom de la variable");
	const infoBrique = {
		positionBrique: calculable.value.positionBrique,
		texte: texteNomVariable.value
	};
	appelerApiModifModele(apiModifierNomVariableCalculable, infoBrique);
}

function modifierExpression() {
	const infoBrique = {
		positionBrique: calculable.value.positionBrique,
		texte: texteExpression.value
	};
	appelerApiModifModele(apiModifierExpression, infoBrique);
}

function modifierTypeFormat(typeFormat) {
	const infosBriques = calculables.value.map(calc => {
		return {positionBrique: calc.positionBrique, texte: typeFormat?.libelle};
	});
	appelerApiModifModele(apiModifierTypeFormatExpressions, infosBriques);
}

</script>

<template>
	<div class="zone-expression" >
		<template v-if="calculable?.nomVariable">
			<label class="intitule">Variable : </label>
			<input
				type="text" 
				class="texte-expression nom-variable" 
				v-model="texteNomVariable" 
				spellcheck="false"
				@keydown.enter.prevent="modifierNomVariable()"
				@keydown.escape.prevent="annuler()"/>
		</template>
		<template v-if="calculable">
			<label class="intitule">{{ getLibelleExpression(calculable) }} : </label>
			<input v-if="modeEdition"
				type="text" 
				spellcheck="false"
				class="texte-expression expression" 
				:class="{'expression-en-erreur': !autoCompletionEstActivee && calculable.expression.texte && !calculable.expression.valide}"
				ref="composantSaisie"
				:disabled="texteExpression == null"
				v-model="texteExpression" 
				@focus="activerAutoCompletion(true)"
				@input="activerAutoCompletion(true)"
				@blur="gererPerteFocus"
				@keydown="gererToucheClavier" />
			<FormuleAffichee v-else
				class="expression"
				:formule="calculable.expression"
				:affichage-suggestions="true"
				@click.stop="activerModeEdition()" />
			<ListeAutoCompletion class="liste-auto-completion" 
				:activee="autoCompletionEstActivee"
				:type-objet="getTypeObjet(calculable.expression.idTypeObjet)"
				:variables="[]"
				:texte="composantSaisie?.value"
				:position-curseur="composantSaisie?.selectionStart"
				:evenement="evenementAutoCompletion"
				@validation="validerAutoCompletion"
				@touche-entree="modifierExpression" />
		</template>
		<template v-if="typesFormatProposes">
			<label class="intitule">Format :</label>
			<SelecteurTypeFormat class="selecteur-type-format"
				v-model:typeFormat="selectionTypeFormat" 
				:types-format-proposes="typesFormatProposes"
				@modification="modifierTypeFormat"/>		
		</template>
	</div>
</template>

<style scoped>

.zone-expression {
	display: flex;
	justify-content: flex-end;
	padding-left: 10px;
	padding-right: 10px;
	flex-grow: 1;
    background-color: var(--couleur-fond-zone-expression);
	border-right: 2px inset whitesmoke;
	min-height: 32px;
}

.nom-variable {
	margin: 5px;
	margin-right: 20px;
	flex-grow: 0;
	align-self: center;
	width: 4em;
}

.expression {
	margin: 5px;
	margin-right: 20px;
	flex-grow: 1;
	align-self: center;
}

.selecteur-type-format {
	margin: 5px;
	flex-grow: 0;
	align-self: center;
	min-width: 100px;
}

.intitule {
	margin-right: 5px;
	flex-grow: 0;
	align-self: center;
}

.liste-auto-completion {
	position: absolute;
	top: 35px;
	left: 300px;
}

.panel-formule {
	border: 1px solid var(--couleur-bordure) !important;
	border-radius: 2px;
	line-height: normal;
	height: 20px;
	cursor: pointer;
}

.panel-formule:hover {
	background: white;
}

</style>